import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { getCredentials } from '../../../actions/hub/hubAuthPagesActions';
import { api } from '../../../api';
import ErrorBlock from '../../../common/ErrorBlock';
import InPageLoader from '../../../common/InPageLoader';
import PageError from '../../../common/PageError';
import AuthPageLayout from '../../../common/hub/AuthPageLayout';
import { logError } from '../../../logging';
import { LOCALSTORAGE_SSO_HANDOFF_SESSION } from '../../../util/constants';
import { PATH_HUB_HOME, PATH_HUB_LOG_IN } from '../../../util/pagePath';
import { getSafeReturnTo } from '../../../util/safeReturnTo';
import { seedCognitoSession } from '../../../util/seedCognitoSession';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const HubSsoCallbackPage = ({ errorMsg, isClockSkewDetected, checkUserStatus }) => {
  const history = useHistory();
  const query = useQuery();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const code = query.get('code');
      const returnTo = getSafeReturnTo(query.get('returnTo'), PATH_HUB_HOME);

      if (!code) {
        history.replace(`${PATH_HUB_LOG_IN}?ssoError=${encodeURIComponent('missing_code')}`);
        return;
      }

      try {
        const tokens = await api.sso.exchangeCode.fetchMutation({ code });
        if (cancelled) return;

        // Prefer cognito:username from the id token — API username can differ and break Amplify lookup.
        seedCognitoSession({
          idToken: tokens.idToken,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });

        // Mark this browser session as coming from SSO handoff so cold loads
        // re-validate tokens instead of treating Keep-Me-Logged-In as a forced sign-out.
        localStorage.setItem(LOCALSTORAGE_SSO_HANDOFF_SESSION, 'true');

        // Same bootstrap as staff login: Amplify reads seeded localStorage via getCredentials.
        checkUserStatus({
          onSuccess: () => {
            if (!cancelled) {
              history.replace(returnTo);
            }
          },
        });
      } catch (e) {
        console.log('[debug] SSO callback failed:', e);
        const message = e?.message || 'sso_exchange_failed';
        logError(message, null, { message: `SSO callback failed: ${message}` });
        if (!cancelled) {
          history.replace(`${PATH_HUB_LOG_IN}?ssoError=${encodeURIComponent(message)}`);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [query, checkUserStatus, history]);

  if (isClockSkewDetected) {
    return <PageError activePage="redirect-clock-skew-handle" />;
  }

  return (
    <Fragment>
      {errorMsg === '' && <InPageLoader />}
      {errorMsg !== '' && (
        <AuthPageLayout title="Signing you in" subtitle="Completing secure sign-in from Student Hub.">
          <ErrorBlock errorMsg={errorMsg} />
        </AuthPageLayout>
      )}
    </Fragment>
  );
};

HubSsoCallbackPage.propTypes = {
  errorMsg: PropTypes.string,
  isClockSkewDetected: PropTypes.bool.isRequired,
  checkUserStatus: PropTypes.func.isRequired,
};

HubSsoCallbackPage.defaultProps = {
  errorMsg: '',
};

export default connect(
  (state) => {
    return {
      errorMsg: state.ui.apiState.hubAuthPage.errorMsg,
      isClockSkewDetected: state.login.isClockSkewDetected,
    };
  },
  (dispatch) => {
    return {
      checkUserStatus: bindActionCreators(getCredentials, dispatch),
    };
  }
)(HubSsoCallbackPage);
